import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

type VariantKey = 'primary' | 'danger' | 'white' | 'attention';

interface VariantTokens {
  front: string;
  text: string;
  edge: string;
  hint: string;
  progressFrom: string;
  progressTo: string;
  progressStripe: string;
  progressBlend?: 'screen' | 'multiply' | 'normal';
  border?: string;
}

const VARIANT_TOKENS: Record<VariantKey, VariantTokens> = {
  primary: {
    front: '#393afb',
    text: '#ffffff',
    edge: 'linear-gradient(120deg, #1f21a6, #5c5dff)',
    hint: 'rgba(255,255,255,0.35)',
    progressFrom: 'rgba(255,255,255,0.45)',
    progressTo: 'rgba(255,255,255,0.12)',
    progressStripe: 'rgba(255,255,255,0.4)',
    progressBlend: 'screen',
  },
  danger: {
    front: '#ff4d4f',
    text: '#ffffff',
    edge: 'linear-gradient(120deg, #b91c1f, #ff7b7d)',
    hint: 'rgba(255,255,255,0.35)',
    progressFrom: 'rgba(255,255,255,0.5)',
    progressTo: 'rgba(255,255,255,0.14)',
    progressStripe: 'rgba(255,255,255,0.38)',
    progressBlend: 'screen',
  },

  white: {
    front: '#ffffff',
    text: '#0f0f0f',
    edge: 'linear-gradient(120deg, #dcdcff, #ffffff)',
    hint: 'rgba(15,15,15,0.25)',
    progressFrom: 'rgba(57,58,251,0.22)',
    progressTo: 'rgba(57,58,251,0.08)',
    progressStripe: 'rgba(57,58,251,0.35)',
    progressBlend: 'multiply',
    border: '1px solid rgba(15,15,15,0.08)',
  },
  attention: {
    front: '#f59e0b',
    text: '#1f1300',
    edge: 'linear-gradient(120deg, #b45309, #fbbf24)',
    hint: 'rgba(31,19,0,0.32)',
    progressFrom: 'rgba(255,255,255,0.48)',
    progressTo: 'rgba(255,255,255,0.16)',
    progressStripe: 'rgba(255,255,255,0.38)',
    progressBlend: 'screen',
  },
};

const getVariantTokens = (variant?: VariantKey) =>
  VARIANT_TOKENS[variant || 'primary'] || VARIANT_TOKENS.primary;

const SURFACE_DATA_ATTR = 'data-hold-surface';

const stripeShift = keyframes`
  from {
    background-position: 0% 0%, 0 0;
  }
  to {
    background-position: 100% 0%, 24px 0;
  }
`;

export const Surface = styled.span<{ variant?: VariantKey }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  padding: 16px 24px 12px;
  border-radius: 12px;
  width: 100%;

  overflow: hidden;
  background: ${({ variant }) => getVariantTokens(variant).front};
  color: ${({ variant }) => getVariantTokens(variant).text};
  border: ${({ variant }) => getVariantTokens(variant).border || 'none'};
  transform: translateY(-4px);
  transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1);
  z-index: 1;
`;

export const Progress = styled.div<{ variant?: VariantKey }>`
  position: absolute;
  inset: 0;
  width: 0%;
  border-radius: inherit;
  pointer-events: none;
  z-index: 1;
  mix-blend-mode: ${({ variant }) => getVariantTokens(variant).progressBlend || 'screen'};
  background-image: linear-gradient(
    120deg,
    ${({ variant }) => getVariantTokens(variant).progressFrom},
    ${({ variant }) => getVariantTokens(variant).progressTo}
  );
  background-size:
    200% 100%,
    32px 32px;
  animation: ${stripeShift} 1.4s linear infinite;
`;

export const HelperText = styled.span<{ variant?: VariantKey }>`
  font-size: 0.65rem;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: ${({ variant }) =>
    variant === 'white' || variant === 'attention'
      ? 'rgba(15, 15, 15, 0.75)'
      : 'rgba(255, 255, 255, 0.85)'};
  text-transform: uppercase;
  opacity: 0.8;
  z-index: 3;
`;

export const Label = styled.span`
  position: relative;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

export const Icon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
`;

export const Wrapper = styled.button<{ variant?: VariantKey }>`
  position: relative;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  outline-offset: 4px;
  transition: filter 250ms;
  user-select: none;
  touch-action: none;
  width: 100%;
  display: inline-flex;
  align-items: stretch;
  font: inherit;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
  }

  &:hover [${SURFACE_DATA_ATTR}='true'] {
    transform: translateY(-6px);
  }

  &:active [${SURFACE_DATA_ATTR}='true'] {
    transform: translateY(-2px);
    transition: transform 34ms ease;
  }

  &:disabled [${SURFACE_DATA_ATTR}='true'] {
    transform: translateY(0);
  }
`;

export const Edge = styled.span<{ variant?: VariantKey }>`
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: ${({ variant }) => getVariantTokens(variant).edge};
  z-index: 0;
`;
